/**
 * TargetCell — one cell of a slot's four-wide target row.
 *
 * Cell 0 is the PRIMARY (the only one that counts against the budget); 1-3 are
 * backups. The cell is a fixed ~92px block so the whole plan reads as a matrix
 * you scan across, not a list of cards you scroll through.
 *
 * 🔴 THE PRICE INPUT MUST NOT REMOUNT. It is controlled and writes straight to
 * the hook on every onChange, so if React ever unmounts and remounts it — a
 * changing key, a conditional wrapper, a rebuilt row subtree — the field loses
 * focus on the first keystroke and the user types one character at a time. The
 * defenses: SlotRow keys these by cellIdx (the row is a fixed positional matrix
 * of exactly BUCKET_WIDTH, never reordered, so the index IS the identity), the
 * empty/filled branch returns the same <div> shape, and nothing here is keyed
 * on target identity. Read the note in SlotRow before changing either.
 *
 * 🔴 THE STAR IS ON THE AVATAR, AND THE REASON IS THE MEASUREMENT THAT KILLED
 * IT FROM THE HOVER CLUSTER. That cluster is promote / gone / remove at 20px
 * with 4px gaps, absolutely positioned OVER THE NAME. At the narrow end of the
 * two-column layout — 1341px, one pixel above PANEL_BREAKPOINT, where the panel
 * still takes its 372px — the cell is 198.3px and the name column 86.3px, so a
 * fourth button there leaves 34.3px of name readable while hovering, about four
 * characters, against 58.3px at three.
 *
 * The avatar is the space that is actually free: 52px, fixed, decorative, and
 * OUTSIDE the flex flow once the star is absolutely positioned on it. So the
 * control costs the name column exactly nothing — 86.3px before and after —
 * which was the only objection. Do not move it back into the row's flex flow.
 *
 * The star is also the MARKER now; there is no separate edge bar. One gold
 * glyph is unambiguous next to a yellow position chip in a way a gold bar was
 * not, and a marker you can click beats a marker you have to go elsewhere to
 * change.
 */

import React, { useState } from "react";
import { theme } from "../../data/theme";
import PlayerAvatar from "../PlayerAvatar";
import StatsButton from "../StatsButton";
import StarToggle from "../StarToggle";
import LockToggle from "../LockToggle";
import NumberStepper, { normalise, SHIFT_MULTIPLIER } from "./NumberStepper";
import { getPlayerByKey } from "../../utils/playerIndex";

const EMPTY_LABEL = (cellIdx) => (cellIdx === 0 ? "+ SELECT PRIMARY" : `+ BACKUP ${cellIdx}`);

// The name wraps to at most two lines and RESERVES both whether it uses them or
// not. Derived from the same token and line-height the name actually renders
// at, so the reservation cannot drift out of step with the text if either
// changes. A ragged internal layout — a one-line name sitting higher than a
// two-line one beside it — would undo the readability the wrap is meant to buy.
const NAME_LINES = 2;
const NAME_LINE_HEIGHT = 1.25;
const NAME_BLOCK_HEIGHT = Math.round(theme.fontSize.sub * NAME_LINE_HEIGHT * NAME_LINES);

/**
 * The board price and your distance from it, as ONE element.
 *
 * 🔴 THEY WERE THE SAME FACT TWICE. Your price is in the field beside this, so
 * board = price − delta: given any two of the three, the third is arithmetic.
 * Two flex items, two gaps and a chip's worth of padding were being spent to
 * say one thing, in the tightest space in the app.
 *
 * Both numbers still SHOW — this removes chrome, not information — but they
 * share an item, so they cost one gap instead of two and the delta loses its
 * background. The colour is a second encoding of the sign, never the only one:
 * the sign itself is written.
 *
 * 🔴 IT ELLIPSES RATHER THAN WRAPPING, AND CARRIES A TITLE. This is the one
 * element in the row that may be cut, because its value is the one recoverable
 * from the other two. Silent truncation is not acceptable anywhere here, so the
 * full sentence is always on the title — the same rule the plan name follows.
 */
function BoardPrice({ market, delta }) {
  return (
    <span
      title={delta
        ? `Board price $${market} — your price is $${Math.abs(delta)} ${delta > 0 ? "over" : "under"} it`
        : `Board price $${market} — your price matches it`}
      style={{
        // minWidth 0 is what lets this be the element that gives when the cell
        // narrows. Without it the row's min-content stays above the cell width
        // and the grid track goes back to overflowing.
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontSize: theme.fontSize.small,
        color: theme.colors.text.faint,
      }}
    >
      board ${market}
      {delta ? (
        <span style={{
          marginLeft: 3,
          fontWeight: theme.fontWeight.bold,
          color: delta > 0 ? theme.colors.status.danger : theme.colors.status.success,
        }}>{delta > 0 ? `+${delta}` : delta}</span>
      ) : null}
    </span>
  );
}

function ActionButton({ label, title, onClick, danger }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      style={{
        width: 20, height: 20, lineHeight: "18px", padding: 0,
        borderRadius: theme.radius.sm,
        border: `1px solid ${theme.colors.border.default}`,
        background: theme.colors.bg.elevated,
        color: danger ? theme.colors.status.danger : theme.colors.text.secondary,
        fontSize: theme.fontSize.small,
        cursor: "pointer",
      }}
    >{label}</button>
  );
}

const PPG_STEP = 0.1;
const PPG_DECIMALS = 1;

/**
 * PpgField — the projection you already read, made editable without costing the
 * row a pixel.
 *
 * 🔴 THE ARROWS ARE REVEALED, NOT ADDED. An inline NumberStepper here measured
 * +26px per card, which moved the whole matrix's requirement from 1677px to
 * 1779px — past the laptop this is drafted on. So the figure keeps its exact
 * width and stays always-visible, and the stepper appears in an absolutely
 * positioned overlay on hover or focus. Out of the flow means out of the
 * measurement: card-check is unmoved by this control existing.
 *
 * It is the same trade the promote / gone / remove cluster already makes, and
 * the same NumberStepper the player list uses — so the override ink, the ×
 * that removes an override rather than freezing today's number, hold-to-run and
 * shift-for-1.0 all arrive already built and already behaving identically in
 * both places.
 *
 * 🔴 AND IT STEPS BY KEYBOARD WITHOUT THE OVERLAY. At a tenth per press this is
 * the control that gets used most while tuning, and reaching for a mouse to
 * nudge a number twenty times is the thing that makes people stop tuning. The
 * figure is focusable; up and down step it, shift steps by ten.
 *
 * The guard on e.target is load-bearing: NumberStepper handles the same keys on
 * its own root, and without it a press inside the overlay would bubble here and
 * step twice.
 */
function PpgField({ ppg, override, base, name, onChange, onReset }) {
  const [active, setActive] = useState(false);
  const overridden = override !== undefined;
  const effective = overridden ? override : base;

  const stepBy = (dir, shift) => {
    if (!onChange) return;
    onChange(normalise(effective + dir * PPG_STEP * (shift ? SHIFT_MULTIPLIER : 1), PPG_DECIMALS));
  };

  return (
    <span
      data-testid="target-ppg"
      tabIndex={0}
      aria-label={`${name} projected points per game`}
      title={`${overridden ? "Your" : "Mock1's"} projected points per game for ${name}. Arrow keys step by 0.1, shift by 1.0. This number is LEAGUE-WIDE — it is the same one the player list and the optimizer read.`}
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      onFocus={() => setActive(true)}
      onBlur={(e) => {
        // Focus moving INTO the overlay must not close it.
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setActive(false);
      }}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;   // the stepper owns its own keys
        if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
        e.preventDefault();
        stepBy(e.key === "ArrowUp" ? 1 : -1, e.shiftKey);
      }}
      style={{ position: "relative", display: "inline-flex", outline: "none", flexShrink: 0 }}
    >
      {/* The always-visible figure. It keeps its width in BOTH states — hidden
          rather than removed — so revealing the stepper cannot reflow the row
          under the cursor. */}
      <span style={{
        fontSize: theme.fontSize.small,
        whiteSpace: "nowrap",
        // Your ink once you have an opinion, the same signal the player list
        // uses for the same fact.
        color: overridden ? theme.colors.brand.orange : theme.colors.text.faint,
        fontWeight: overridden ? theme.fontWeight.bold : theme.fontWeight.regular,
        visibility: active ? "hidden" : "visible",
      }}>{effective.toFixed(PPG_DECIMALS)} PPG</span>

      {active && (
        <span style={{
          position: "absolute", left: -4, top: "50%", transform: "translateY(-50%)",
          zIndex: 3,
          display: "inline-flex", alignItems: "center",
          padding: "1px 3px",
          borderRadius: theme.radius.sm,
          // Its own surface: it sits over the card's numbers row, and a
          // transparent overlay would read as two numbers on top of each other.
          background: theme.colors.bg.elevated,
          border: `1px solid ${theme.colors.border.default}`,
        }}>
          <NumberStepper
            value={override}
            base={base}
            step={PPG_STEP}
            decimals={PPG_DECIMALS}
            width={26}
            title="Your projected points per game — league-wide. Hold to run, shift for 1.0, × to go back to the projection."
            onChange={onChange}
            onReset={onReset}
          />
        </span>
      )}
    </span>
  );
}

export default function TargetCell({
  target,
  cellIdx,
  isActiveCell = false,
  isPrimary = false,
  starred = false,
  onToggleStar,
  locked = false,
  onToggleLock,
  posRank = null,
  ppg = 0,
  // The override, or undefined when the card is showing Mock1's projection, and
  // the engine's own number as the base the × goes back to.
  ppgOverride,
  enginePpg = 0,
  onPpgChange,
  onPpgReset,
  onSelect,
  selectOnTap = false,
  onPromote,
  onToggleGone,
  onRemove,
  onPriceChange,
}) {
  const [hovered, setHovered] = useState(false);

  // ── Empty ────────────────────────────────────────────────────────────────
  // The active cell goes solid with an orange tint so the panel on the right
  // and the cell it is filling read as one thing across the width of the page.
  if (!target) {
    return (
      <div
        onClick={() => onSelect && onSelect(cellIdx)}
        style={{
          // Matches the filled card's minHeight so an empty cell in a row of
          // wrapped ones does not sit short. Grid stretch does the rest.
          minHeight: 108,
          display: "flex", alignItems: "center", justifyContent: "center",
          borderRadius: theme.radius.lg,
          border: isActiveCell
            ? `1px solid ${theme.colors.brand.orange}`
            : `1px dashed ${theme.colors.border.default}`,
          background: isActiveCell ? theme.helpers.brandBg(0.08) : theme.colors.bg.surface,
          color: isActiveCell ? theme.colors.brand.orange : theme.colors.text.faint,
          fontSize: theme.fontSize.small,
          fontWeight: theme.fontWeight.bold,
          letterSpacing: 0.6,
          cursor: "pointer",
        }}
      >
        {EMPTY_LABEL(cellIdx)}
      </div>
    );
  }

  // ── Filled ───────────────────────────────────────────────────────────────
  const delta = Math.round(target.price - target.market);

  // 🔴 isPrimary comes from SlotRow's primaryOf, NOT from cellIdx === 0.
  // primaryOf falls through — remove the occupant of cell 0, or mark them gone,
  // and backup 1 becomes the primary. The BUDGET already treats it that way
  // (planTotals reads primaryOf), so badging cell 0 regardless made the math and
  // the screen disagree: an empty or struck-through cell wearing PRIMARY while
  // the money came from the cell beside it.
  //
  // The row is NOT compacted on remove to make this true. Cells are positional
  // by design and index-keyed; shifting them would churn the very keys the
  // price-input focus fix depends on.

  /**
   * 🔴 GONE BEATS STARRED — FOR THE SURFACE, NOT FOR THE CONTROL. The raised
   * surface is a "look here" signal you read while scanning the grid, and a man
   * who is off the board is the last thing that should catch your eye; of the
   * two facts, gone is the one you need at an auction. So a gone card drops back
   * to the base surface and keeps its dimming.
   *
   * The STAR ITSELF stays rendered and stays clickable, because you are allowed
   * to change your mind about someone who went — and because a control that
   * disappears when a card changes state is a control you cannot trust. It dims
   * with the card, which is unavoidable (opacity applies to the whole subtree)
   * and correct: every other control on a gone card is in the same state.
   *
   * 🔴 AND THE STAR IS NOT THE PRIMARY MARKER. PRIMARY owns the orange border
   * and the corner badge; the star owns the avatar and the surface. Different
   * marks in different places, so a starred backup and an unstarred primary
   * cannot be confused and a card can wear both at once.
   */
  const accented = starred && !target.gone;

  /**
   * 🔴 LOCKED TAKES THE BADGE FROM PRIMARY, and that is not a loss of
   * information. Only a primary can be locked — toggleLockInBucket promotes
   * first — so LOCKED implies PRIMARY and strictly outranks it: at a live
   * auction "this one is already mine" is the fact you need off a glance, and
   * "this is the one that counts against the budget" is the one you can infer.
   * One badge in one place beats two competing for the same corner.
   *
   * The card's border and surface go steel for the same reason the control is
   * a whole-cell treatment rather than an icon: a locked row has to read from
   * across a table, not from an 18px glyph.
   */
  const lockTint = locked ? theme.helpers.lockBg(0.10) : null;

  /**
   * 🔴 A FILLED CARD IS TAPPABLE ON A PHONE ONLY, and the default is off so the
   * desktop card keeps having no root click handler at all. It needs one on a
   * phone because the promote / gone / remove cluster below is HOVER-GATED and
   * therefore invisible on touch, which left a row that already had a player
   * with no route to the list at its own slot.
   *
   * Every control inside the card stops its own propagation (see the guards
   * below), so tapping a stepper, a star or the lock still does only its own
   * job. StatsButton already did.
   */
  const rootSelect = selectOnTap && onSelect ? (e) => {
    // 🔴 ONE GUARD, NOT A stopPropagation ON EVERY CONTROL. The card holds a
    // price input, two stepper widgets, a star, a lock and the action cluster,
    // and adding a handler to each is six places for the seventh to be
    // forgotten. Asking the event where it landed asks the question once.
    //
    // `target-ppg` is named explicitly because it is a focusable SPAN, not a
    // button or an input: the figure is always drawn and the stepper only
    // appears once it is active, so a tap on the number itself has no control
    // element under it to match.
    const el = e.target;
    if (el && el.closest && el.closest('button, input, [data-testid="target-ppg"]')) return;
    onSelect(cellIdx);
  } : undefined;

  return (
    <div
      data-testid="target-card"
      onClick={rootSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        // 🔴 minHeight, NOT height — the vertical half of the same fix.
        //
        // The numbers row wraps at the narrow end of the grid (see below), and a
        // fixed 108 gave the column 90px for 99px of wrapped content: the PPG
        // line ran out of the bottom of the card. Cards in a row stretch
        // together, so this grows the whole row by ~9px at widths where the wrap
        // happens and changes NOTHING at widths where it does not — which is the
        // monitor this is normally used on.
        //
        // Measured by card-check, which fails if the column ever overflows the
        // box again.
        minHeight: 108,
        display: "flex",
        gap: theme.spacing.md,
        padding: theme.spacing.md,
        borderRadius: theme.radius.lg,
        // 🔴 THE PRIMARY WEARS THE FRAME, AND IT IS A FULL-STRENGTH ONE. Every
        // cell in the row is the same size and the same shape, so the one that
        // costs you money has to be legible as that from a scan of the whole
        // page rather than from reading the badge. brandLight (0.2) against
        // subtle was a difference you found by looking for it; border.brand
        // plus a faint fill is one you see. A LOCK still outranks it: settled
        // beats chosen.
        border: `1px solid ${locked
          ? theme.colors.lock
          : (isPrimary ? theme.colors.border.brand : theme.colors.border.subtle)}`,
        // Raised a step off bg.surface, so a starred row reads while you are
        // scanning the grid rather than only when you look at one cell. A lock
        // outranks the star here: both can be true at once, and the star's job
        // is "look here" while the lock's is "this one is settled".
        // The star's raised surface still wins over the primary tint: both are
        // "look here" signals and the star is the one you set yourself.
        background: lockTint
          || (accented ? theme.colors.bg.elevated
            : (isPrimary ? theme.helpers.brandBg(0.05) : theme.colors.bg.surface)),
        opacity: target.gone ? 0.42 : 1,
        boxSizing: "border-box",
      }}
    >
      {/* The real headshot. The Target carries no sleeper_id (see
          utils/playerIndex for why), so the full record is looked up by the
          same composite key dynamicPrices uses. On a miss — a plan outliving a
          player leaving the pool — the Target itself is passed instead, which
          still has name/pos/team, and PlayerAvatar falls back to its
          pos-colored initials circle. */}
      {/* 🔴 position:relative and NOTHING ELSE changes here. The star is
          absolutely positioned inside this span, so it never enters the row's
          flex flow and the name column keeps every pixel it had — which is the
          whole reason the control lives on the avatar and not in the hover
          cluster. Adding it as a flex child would re-create the problem the
          measurement in the header rules out.

          🔴 BOTTOM-left of the avatar, not top-left. Top-left is where the
          PRIMARY badge sits — absolute at the CARD's 0,0, about 62 x 14px —
          and the avatar's own top-left corner is at card (8,8), so a 16px star
          there would run under the badge for its top 6px. The badge background
          is translucent orange, so it would not even hide the collision, it
          would tint it. Bottom-left is equally free, has the same zero cost to
          the name, and collides with nothing: the hover cluster is top-RIGHT
          and the price row starts to the avatar's right. */}
      <span data-testid="target-avatar" style={{ display: "flex", flexShrink: 0, position: "relative" }}>
        <PlayerAvatar player={getPlayerByKey(target.key) || target} size={52} />
        <StarToggle
          starred={starred}
          name={target.name}
          size={18}
          variant="overlay"
          testId="target-star-toggle"
          onToggle={() => onToggleStar && onToggleStar(cellIdx)}
          style={{ position: "absolute", left: -3, bottom: -3 }}
        />
        {/* 🔴 BOTTOM-RIGHT, and the measurement is the same one that put the
            star bottom-left. The avatar is 52px at card (8,8), so an 18px
            button at right:-3 spans card x 45-63 — five pixels clear of the
            text column, which starts at 68 (a 52px avatar plus an 8px gap). It
            costs the name column nothing, which is the whole reason both
            controls live on the avatar and not in the hover cluster: a fourth
            button THERE leaves 34.3px of name readable while hovering, against
            58.3px at three.

            The two buttons' inner edges sit 22px apart on the 52px avatar, so
            they cannot be mis-hit for each other in a hurry. Top-left remains
            unavailable — that corner is the badge's. */}
        <LockToggle
          locked={locked}
          name={target.name}
          price={target.price}
          size={18}
          variant="overlay"
          testId="target-lock-toggle"
          onToggle={() => onToggleLock && onToggleLock(cellIdx)}
          style={{ position: "absolute", right: -3, bottom: -3 }}
        />
      </span>

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        {/* Two lines, not one ellipsed line. This is a tool for scanning names
            and "Christian McCa…" fails at the only job the cell has. The clamp
            supplies its own ellipsis on the second line, so the title stays for
            the rare name that needs a third.

            🔴 No hover padding. It used to reserve 68px for the action cluster,
            which was free when the name was a single nowrap line — it just
            ellipsed sooner. With wrapping it would RE-FLOW the name the moment
            you moved the mouse over the cell, re-clamping a long name down to a
            few characters. A stable name that the buttons briefly overlap beats
            a name that rearranges itself under the cursor; the buttons are
            opaque and you are hovering to act, not to read. */}
        <div title={target.name} style={{
          fontSize: theme.fontSize.sub,
          fontWeight: theme.fontWeight.bold,
          color: theme.colors.text.primary,
          textDecoration: target.gone ? "line-through" : "none",
          lineHeight: NAME_LINE_HEIGHT,
          minHeight: NAME_BLOCK_HEIGHT,
          display: "-webkit-box",
          WebkitLineClamp: NAME_LINES,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}>
          {target.name}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: theme.spacing.sm, minWidth: 0 }}>
          <span style={{
            ...theme.helpers.posStyle(target.pos),
            padding: "1px 5px",
            borderRadius: theme.radius.sm,
            fontSize: theme.fontSize.caption,
            fontWeight: theme.fontWeight.heavy,
            letterSpacing: 0.5,
          }}>{target.pos}</span>
          <span style={{
            fontSize: theme.fontSize.small,
            color: theme.colors.text.faint,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {target.team || "FA"} · {posRank || target.pos}
          </span>
        </div>

        {/* 🔴 IT WRAPS WHEN SQUEEZED, AND THAT IS THE FIX RATHER THAN A
            FALLBACK. At the narrow end of the grid the cell is ~198px, which
            leaves this row about 86px — and the price field alone measures 65px
            while "24.3 PPG" measures 48px, so ONE LINE WAS NEVER POSSIBLE
            there, whatever got cut. Two lines cost no card height: the column
            is space-between over 92px holding 69px of content, so the second
            line lands in slack that already existed.

            Everything stays visible and nothing is clipped. At any width where
            the row fits on one line it still renders on one line, so the wrap
            is invisible on the monitor this is usually used on. */}
        <div style={{
          display: "flex", alignItems: "center", gap: theme.spacing.sm,
          flexWrap: "wrap", rowGap: 1, minWidth: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
            <span style={{ fontSize: theme.fontSize.small, color: theme.colors.text.faint }}>$</span>
            {/* 🔴 READ-ONLY WHEN LOCKED, rather than silently ignored. Neither
                the price sheet (applySheetToPlans) nor a board resync rewrites
                a locked price — so an editable field here would change the
                number under the cursor and then have it snap back, which is the
                worst of the three available behaviours. Unlock, retype,
                re-lock: three clicks, every one of which says what it does. */}
            <input
              type="number"
              min={0}
              value={target.price}
              readOnly={locked}
              title={locked ? "Locked at what you paid — unlock to change it" : undefined}
              onFocus={(e) => e.target.select()}
              onChange={(e) => { if (!locked && onPriceChange) onPriceChange(cellIdx, e.target.value); }}
              style={{
                width: 46,
                padding: "2px 4px",
                borderRadius: theme.radius.sm,
                border: `1px solid ${locked ? theme.colors.lock : theme.colors.border.default}`,
                background: theme.colors.bg.raised,
                color: theme.colors.text.primary,
                cursor: locked ? "default" : "text",
                fontSize: theme.fontSize.small,
                fontWeight: theme.fontWeight.bold,
                fontFamily: theme.fonts.body,
              }}
            />
          </div>
          <BoardPrice market={target.market} delta={delta} />
          {/* Mock1's own projection, season points over 17 — see ppgFor in
              DraftPlan. LABELLED, because an unlabelled "14.2" sitting beside
              "$46" and "board $51" reads as another dollar figure. Nothing is
              rendered at 0: a lookup miss or an unprojected player should be
              silent rather than claim a confident 0.0. */}
          {/* 🔴 EDITABLE EVEN WHEN THE CARD IS LOCKED, unlike the price field
              above it. A lock is a statement about money — what he cost you —
              so the price is settled. It says nothing about how good you think
              he will be, and revising that after you own him is an ordinary
              thing to do mid-draft. */}
          {onPpgChange && (ppg > 0 || ppgOverride !== undefined) ? (
            <PpgField
              ppg={ppg}
              override={ppgOverride}
              base={enginePpg}
              name={target.name}
              onChange={(v) => onPpgChange(cellIdx, v)}
              onReset={() => onPpgReset && onPpgReset(cellIdx)}
            />
          ) : ppg > 0 && (
            <span style={{ fontSize: theme.fontSize.small, color: theme.colors.text.faint, whiteSpace: "nowrap" }}>
              {ppg.toFixed(1)} PPG
            </span>
          )}
        </div>
      </div>

      {/* Right side of the row, same slot PlayerList gives it. DEF and K are
          suppressed exactly as PlayerList does — there is no game log to open.
          StatsButton stopPropagation's its own click, which is belt-and-braces
          here: only the EMPTY branch has a root onClick, so a filled cell has
          no select handler for it to trigger. */}
      {target.pos !== "DEF" && target.pos !== "K" && (
        <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          <StatsButton name={target.name} source="draft_plan" />
        </div>
      )}

      {(isPrimary || locked) && (
        <span style={{
          position: "absolute", top: 0, left: 0,
          padding: "1px 6px",
          borderTopLeftRadius: theme.radius.lg,
          borderBottomRightRadius: theme.radius.md,
          background: locked ? theme.helpers.lockBg(0.20) : theme.helpers.brandBg(0.16),
          color: locked ? theme.colors.lock : theme.colors.brand.orange,
          fontSize: theme.fontSize.caption,
          fontWeight: theme.fontWeight.heavy,
          letterSpacing: 0.7,
        }}>{locked ? "LOCKED" : "PRIMARY"}</span>
      )}

      {hovered && (
        <div style={{ position: "absolute", top: theme.spacing.xs, right: theme.spacing.xs, display: "flex", gap: theme.spacing.xs }}>
          {/* cellIdx > 0 as well as !isPrimary: promoteInBucket refuses index 0
              (there is nothing above it to swap into), so a struck-through cell
              0 sitting under an effective primary in cell 1 would otherwise
              render a button that silently does nothing. Un-marking it gone is
              the way back, and primaryOf hands it the badge again. */}
          {!isPrimary && cellIdx > 0 && !locked && (
            <ActionButton label="▲" title="Promote to primary" onClick={() => onPromote && onPromote(cellIdx)} />
          )}
          {/* 🔴 BOTH HIDDEN ON A LOCKED CARD, because both are already no-ops
              on one. toggleGoneInBucket refuses a locked cell outright — you
              cannot have won him and lost him — and a locked cell is always the
              primary, so there is nothing above it to promote into. A button
              that does nothing when pressed is worse than no button.
              REMOVE stays: locking the wrong man is a mistake somebody will
              make in a hurry, and taking the card off the plan is the way out
              of it. */}
          {!locked && (
            <ActionButton label="⊘" title={target.gone ? "Mark available" : "Mark gone"} onClick={() => onToggleGone && onToggleGone(cellIdx)} />
          )}
          <ActionButton label="✕" title="Remove" danger onClick={() => onRemove && onRemove(cellIdx)} />
        </div>
      )}
    </div>
  );
}
